import React, { Component } from 'react';
import { useSelector } from 'react-redux';

import { css } from 'glamor';

import {
  init as initConnection,
  send,
} from 'loot-core/src/platform/client/fetch';

import { useActions } from '../hooks/useActions';
import installPolyfills from '../polyfills';
import { ResponsiveProvider } from '../ResponsiveProvider';
import { styles, hasHiddenScrollbars, ThemeStyle } from '../style';

import AppBackground from './AppBackground';
import DevelopmentTopBar from './DevelopmentTopBar';
import FatalError from './FatalError';
import FinancesApp from './FinancesApp';
import ManagementApp from './manager/ManagementApp';
import MobileWebMessage from './MobileWebMessage';
import UpdateNotification from './UpdateNotification';

class App extends Component {
  state = {
    fatalError: null,
    initializing: true,
    hiddenScrollbars: hasHiddenScrollbars(),
  };

  async init() {
    const socketName = await global.Actual.getServerSocket();

    try {
      await initConnection(socketName);
    } catch (e) {
      if (e.type === 'app-init-failure') {
        this.setState({ initializing: false, fatalError: e });
        return;
      } else {
        throw e;
      }
    }

    // Load any global prefs
    await this.props.loadGlobalPrefs();

    // Open the last opened budget, if any
    const budgetId = await send('get-last-opened-backup');
    if (budgetId) {
      await this.props.loadBudget(budgetId);

      // Check to see if this file has been remotely deleted (but
      // don't block on this in case they are offline or something)
      send('get-remote-files').then(files => {
        if (files) {
          let remoteFile = files.find(f => f.fileId === this.props.cloudFileId);
          if (remoteFile && remoteFile.deleted) {
            this.props.closeBudget();
          }
        }
      });
    }
  }

  async componentDidMount() {
    await Promise.all([installPolyfills(), this.init()]);

    this.setState({ initializing: false });

    let checkScrollbars = () => {
      if (this.state.hiddenScrollbars !== hasHiddenScrollbars()) {
        this.setState({ hiddenScrollbars: hasHiddenScrollbars() });
      }
    };
    window.addEventListener('focus', checkScrollbars);
    this.cleanup = () => window.removeEventListener('focus', checkScrollbars);
  }

  componentDidCatch(error) {
    this.setState({ fatalError: error });
  }

  componentDidUpdate(prevProps) {
    if (this.props.budgetId !== prevProps.budgetId) {
      global.Actual.updateAppMenu(!!this.props.budgetId);
    }
  }

  render() {
    const { budgetId, loadingText } = this.props;
    const { fatalError, initializing, hiddenScrollbars } = this.state;

    return (
      <ResponsiveProvider>
        <div
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {process.env.REACT_APP_REVIEW_ID && <DevelopmentTopBar />}
          <div
            key={hiddenScrollbars ? 'hidden-scrollbars' : 'scrollbars'}
            {...css([
              {
                flexGrow: 1,
                overflow: 'hidden',
              },
              styles.lightScrollbar,
            ])}
          >
            {fatalError ? (
              <>
                <AppBackground />
                <FatalError error={fatalError} buttonText="Restart app" />
              </>
            ) : initializing ? (
              <AppBackground
                initializing={initializing}
                loadingText={loadingText}
              />
            ) : budgetId ? (
              <FinancesApp />
            ) : (
              <>
                <AppBackground
                  initializing={initializing}
                  loadingText={loadingText}
                />
                <ManagementApp isLoading={loadingText != null} />
              </>
            )}

            <UpdateNotification />
            <MobileWebMessage />
          </div>
        </div>
        <ThemeStyle />
      </ResponsiveProvider>
    );
  }
}

export default function AppWrapper() {
  let budgetId = useSelector(
    state => state.prefs.local && state.prefs.local.id,
  );
  let cloudFileId = useSelector(
    state => state.prefs.local && state.prefs.local.cloudFileId,
  );
  let loadingText = useSelector(state => state.app.loadingText);
  let { loadBudget, closeBudget, loadGlobalPrefs } = useActions();

  return (
    <App
      budgetId={budgetId}
      cloudFileId={cloudFileId}
      loadingText={loadingText}
      loadBudget={loadBudget}
      closeBudget={closeBudget}
      loadGlobalPrefs={loadGlobalPrefs}
    />
  );
}
